import React, { useEffect, useState, useCallback, useRef } from "react";
import Input from "../../components/controls/input/customInput/CustomInput";
import invoiceRegistryService from "../../store/services/invoiceRegistry.service";
import projectService from "../../store/services/projects.service";
import Searchable from "../../components/controls/select/CustomSelect";
import _ from "lodash";
import { ColumnConfigProvider } from "../../components/controls/tables/ColumnConfigProvider";
import { calculatePageSize, changeRequestTypes, getLabelByType } from "../../helpers/utils";
import Table from "../../components/controls/tables/Table";
import { useDispatch } from "react-redux";
import { generalActions } from "../../store/actions";
import RichTextEditor from "../../components/richTextEditor/RichTextEditor";
import { CalendarIcon } from "../../modules/default-svg";
import {
  getAccountCurrency,
  displayDecimalFormatted,
} from "../../utils/systemSettings";
import {
  createInvoiceRegistryRequest,
  deleteInvoiceRegistryRequest,
  getInvoiceRegistryRequest,
  updateInvoiceRegistryRequest,
} from "../../store/actions/invoiceRegistry.actions";
import Skeleton from "react-loading-skeleton";
import { debounce } from "lodash";
import EntityFileUpload from "../../components/controls/fileUpload/EntityFileUpload";
import ModalCheckbox from "../../components/controls/modalcheckbox/ModalCheckbox";

const InvoiceRegistry = (props) => {
  const { projectID, allInvoiceRegistries, loading, pagination, pageLoading } =
    props;

  const dispatch = useDispatch();
  const settings = JSON.parse(localStorage.getItem("settings"));
  const [data, setData] = useState([]);
  const [perPageItems] = useState(calculatePageSize());
  const [pageNum, setPageNum] = useState(1);
  const [selectedRow, setSelectedRow] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [isCreate, setIsCreate] = useState(false);
  const [isCreateNewRow, setIsCreateNewRow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteId, setDeletedId] = useState(null);
  const [invoiceRegistry, setInvoiceRegistry] = useState({
    id: "",
    date: null,
    dueDate: null,
    paidDate: null,
    supplierInvId: "",
    internalInvId: "",
    valueBeforeTax: 0,
    account: "",
    comments: "",
    purchaseOrderId: "",
    cashflowLineItemId: "",
    isInLedger: 0,
  });
  const [attachments, setAttachments] = useState([]);
  const [cashflowLineItem, setCashflowLineItem] = useState({
    id: "",
    month: 0,
    inv: 0,
    ac: 0,
    lineId: "",
    lineCategory: null,
    lineDescription: "",
    label: "",
    cashflowId: "",
  });
  const [cashflowLineOptions, setCashflowLineOptions] = useState([]);
  const [formErrors, setFormErrors] = useState({
    po: "",
    p_code: "",
    date: "",
  });

  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [purchaseOrder, setPurchaseOrder] = useState({
    id: "",
    name: "",
    vendor: "",
    value: 0,
    changeRequest: null,
  });
  const [tableSearch, setTableSearch] = useState(null);

  const fetchData = (pageNumber, pageSize, searchTerm, forceClearSearch = false) => {
    const payload = { page: pageNumber ?? pageNum, size: pageSize ?? perPageItems, search: forceClearSearch ? null : (searchTerm ?? tableSearch) };
    payload.parent = "Project";

    if (projectID) {
      payload.id = projectID;
      payload.parent = "Project";
    }
    else {
      setData([]);
      return;
    }

    dispatch(getInvoiceRegistryRequest(payload));
  };
  const handleAddRow = useCallback(() => {
    setIsCreateNewRow(true);
    fetchData(pagination.TotalPages ?? 1, perPageItems > 10 ? perPageItems - 5 : perPageItems - 3);
  }, []);

  const handleDelete = (selectedRow) => {
    const id = selectedRow.id;
    dispatch(
      deleteInvoiceRegistryRequest({
        id: id,
        parentID: projectID,
        parent: "Project",
      })
    );
    setDeletedId(id);
  };

  const initDataState = () => {
    setFormErrors({});
    setInvoiceRegistry({
      id: "",
      date: null,
      dueDate: null,
      paidDate: null,
      supplierInvId: "",
      internalInvId: "",
      valueBeforeTax: 0,
      account: "",
      comments: "",
      purchaseOrderId: "",
      cashflowLineItemId: "",
      isInLedger: 0,
    });
    setAttachments([]);
    setCashflowLineItem({
      id: "",
      month: 0,
      inv: 0,
      ac: 0,
      lineId: "",
      lineCategory: null,
      lineDescription: "",
      label: "",
      cashflowId: "",
    });
    setPurchaseOrder({
      id: "",
      name: "",
      vendor: "",
      value: 0,
      changeRequest: null
    });
    setPurchaseOrders([]);
    setCashflowLineOptions([]);
  };

  const handleSave = () => {
    const errors = {};
    if (!invoiceRegistry.purchaseOrderId) errors["po"] = `Required`;
    if (!cashflowLineItem.lineId) errors["p_code"] = `Required`;
    if (!invoiceRegistry.date) errors["date"] = `Required`;

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    if (!isEdit) invoiceRegistry.projectId = projectID;

    invoiceRegistry.cashflowLineId = cashflowLineItem.lineId;

    if (Object.keys(selectedRow).length > 0) {
      dispatch(
        updateInvoiceRegistryRequest({
          id: selectedRow.id,
          parentID: projectID,
          parent: "Project",
          payload: invoiceRegistry,
          perPageItems,
        })
      );
      return;
    } else {
      dispatch(
        createInvoiceRegistryRequest({
          parentID: projectID,
          parent: "Project",
          payload: invoiceRegistry,
          perPageItems,
        })
      );
    }
  };

  const fetchInvoiceRegistryData = async () => {
    setIsLoading(true);
    invoiceRegistryService
      .fetchSingleInvoiceRegistryRequest(selectedRow.id)
      .then((data) => {
        setInvoiceRegistry({
          id: data.id,
          date: data.date,
          dueDate: data.dueDate,
          paidDate: data.paidDate,
          supplierInvId: data.supplierInvId,
          internalInvId: data.internalInvId,
          valueBeforeTax: data.valueBeforeTax,
          account: data.account,
          comments: data.comments,
          purchaseOrderId: data.purchaseOrder?.id,
          cashflowLineItemId: data.cashflowLineItem?.id,
          isInLedger: data.isInLedger,
        });
        setPurchaseOrders([data.purchaseOrder]);
        setAttachments(data.attachments || []);
        setPurchaseOrder({
          id: data.purchaseOrder?.name,
          name: data.purchaseOrder?.name,
          vendor: data.purchaseOrder?.vendor,
          value: data.purchaseOrder?.value,
          changeRequest: data.purchaseOrder?.changeRequest
        });
        setCashflowLineItem({
          id: data.cashflowLineItem?.id,
          month: data.cashflowLineItem?.month,
          inv: data.cashflowLineItem?.inv,
          ac: data.cashflowLineItem?.ac,
          lineId: data.cashflowLineItem?.lineId,
          lineCategory: data.cashflowLineItem?.lineCategory,
          lineDescription: data.cashflowLineItem?.lineDescription,
          label: `${getLabelByType("cashflowLineCategory", data.cashflowLineItem?.lineCategory)} - ${data.cashflowLineItem?.lineDescription}`,
          cashflowId: data.cashflowLineItem?.cashflowId,
        });
        if (data.cashflowLineItem?.lineId)
          handleCashflowLineChange(
            {
              value: data.cashflowLineItem?.lineId,
              label: `${getLabelByType("cashflowLineCategory", data.cashflowLineItem?.lineCategory)} - ${data.cashflowLineItem?.lineDescription}`,
            },
            true
          );
      })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title:
              "Something went wrong fetching data. Please try again later.",
            type: "error",
          })
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  const handleCancel = () => {
    setIsEdit(false);
    initDataState();
  };

  useEffect(() => {
    if (Object.keys(selectedRow).length > 0) {
      initDataState();
      isEdit && fetchInvoiceRegistryData();
    }
  }, [selectedRow, isEdit]);

  useEffect(() => {
    fetchData();
  }, [pageNum, perPageItems]);

  const handleCancelAddRow = () => {
    setIsCreateNewRow(false);
    fetchData();
  };

  const handleSearch = (filteredData) => {
    setData(filteredData);
  };

  useEffect(() => {
    if (allInvoiceRegistries && allInvoiceRegistries.length > 0) {
      if (isCreateNewRow) {
        setData(allInvoiceRegistries.sort((a, b) => b.isFlagged - a.isFlagged));
        setIsCreateNewRow(false);
      } else {
        if (pageNum === 1) {
          setData(
            allInvoiceRegistries
              .filter((item) => item.id !== deleteId)
              .sort((a, b) => {
                return b.isFlagged - a.isFlagged;
              })
          );
        }
        else {
          setData((prevData) => {
            const newMap = new Map(
              allInvoiceRegistries.map((item) => [item.id, item])
            );

            const updatedPrevData = prevData.map((item) =>
              newMap.has(item.id) ? newMap.get(item.id) : item
            );

            const prevDataIds = new Set(prevData.map((item) => item.id));
            const newItems = allInvoiceRegistries.filter(
              (shift) => !prevDataIds.has(shift.id)
            );

            return [...updatedPrevData, ...newItems]
              .filter((item) => item.id !== deleteId)
              .sort((a, b) => b.isFlagged - a.isFlagged);
          });
        }

      }
    } else if (allInvoiceRegistries && allInvoiceRegistries.length === 0) {
      setData([]);
    }
  }, [allInvoiceRegistries]);

  const handleCreate = (val) => {
    initDataState();
    setIsCreate(val);
  };

  const handleInputChange = (name, value) => {
    setInvoiceRegistry((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleCashflowLineItemChange = (name, value) => {
    setCashflowLineItem((prev) => ({ ...prev, [name]: value }));
  };
  // const handlePurchaseOrderChange = (name, value) => {
  //   setPurchaseOrder((prev) => ({ ...prev, [name]: value }));
  // };
  const handleCashflowLineChange = (line, isInit = false) => {
    handleCashflowLineItemChange("lineId", line?.value);
    handleCashflowLineItemChange("label", line?.label);

    if (!line?.value) {
      return;
    }
  };
  const cashflowLineSearchdebounced = useRef(
    debounce((value, params) => searchCashflowLineHandler(value, params), 500)
  );
  const handleCashflowLineSearch = useCallback((value, params) => {
    cashflowLineSearchdebounced.current(value, params);
  }, []);

  const poSearchdebounced = useRef(
    debounce((value) => searchPurchaseOrderHandler(value), 500)
  );
  const handlePoSearch = useCallback((value) => {
    poSearchdebounced.current(value);
  }, []);

  const searchCashflowLineHandler = (value, params) => {
    setCashflowLineOptions([]);
    if (!value) value = "";
    const searchParams = { description: value, changeRequestId: params.changeRequestId };
    projectService
      .getProjectCashflowLookup(projectID, searchParams)
      .then((data) => {
        setCashflowLineOptions(data.lines);
      })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title:
              "Something went wrong fetching data. Please try again later.",
            type: "error",
          })
        );
      })
      .finally(() => { });
  };
  const searchPurchaseOrderHandler = (value) => {
    if (!value) value = "";
    projectService
      .getProjectPurchaseOrders(projectID, value)
      .then((data) => {
        setPurchaseOrders(data);
      })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title:
              "Something went wrong fetching data. Please try again later.",
            type: "error",
          })
        );
      })
      .finally(() => { });
  };

  const handlePOChange = (poItem) => {
    let isEmptyPO = !poItem;
    let selected;
    if (!isEmptyPO) {
      selected = purchaseOrders.find((item) => item.id === poItem.value);
      if (selected) {
        handleInputChange("purchaseOrderId", selected.id);
        setPurchaseOrder(selected);
      } else {
        isEmptyPO = true;
      }
    }

    if (isEmptyPO) {
      setPurchaseOrder({
        id: "",
        name: "",
        vendor: "",
        value: 0,
        changeRequest: null
      });
      handleInputChange("purchaseOrderId", "");
    }
    handleCashflowLineChange(null);
    handleCashflowLineSearch("", {
      changeRequestId: selected ? selected?.changeRequest?.id : null,
    });
  };

  const formBody = () => {
    return (
      <>
        <div className="d-flex flex-col">
          <div className="row align-items-end w-100 mb-4 gx-3">
            <div className="col-4">
              <ModalCheckbox
                label={invoiceRegistry.isInLedger ? "Included In Ledger" : "Include In Ledger"}
                checked={invoiceRegistry.isInLedger}
                setChecked={(value) => {
                  handleInputChange("isInLedger", value);
                }}
                loading={isLoading}
              />
            </div>
          </div>
          <div className="row w-100 mb-3 gx-3">
            <div className="col-4">
              <Searchable
                label="PO #"
                isRequired
                SelectText="PO"
                selectWd="me-3"
                defaultVal={
                  purchaseOrder?.id
                    ? { value: purchaseOrder.id, label: purchaseOrder.name }
                    : null
                }
                searchText={(value) => {
                  handlePoSearch(value);
                }}
                options={purchaseOrders.map((option) => ({
                  label: option.name,
                  value: option.id,
                }))}
                onChange={(value) => {
                  handlePOChange(value);
                }}
                loading={isLoading}
                errorMessage={formErrors.po}
              />
            </div>
            <div className="row col-8 flex gx-3">
              {purchaseOrder?.id && purchaseOrder?.changeRequest && (
                <div className="flex-1 flex flex-col">
                  <label className="Input_label">Change Request</label>
                  {isLoading ? (
                    <Skeleton height={40} baseColor="#e0e0e0" highlightColor="#ECE7FD" />
                  ) : (
                    <p
                      className="text-[#1F1F1F] bg-[#EDECF8] text-center py-[9px] rounded-md"
                      style={{ border: "1px solid #342C82" }}
                    >
                      {purchaseOrder?.changeRequest?.name || ""}
                    </p>
                  )}
                </div>
              )}
              {purchaseOrder?.id && (
                <div className="flex-1 flex flex-col">
                  <label className="Input_label">Supplier Name</label>
                  {isLoading ? (
                    <Skeleton height={40} baseColor="#e0e0e0" highlightColor="#ECE7FD" />
                  ) : (
                    <p
                      className="text-[#1F1F1F] bg-[#EDECF8] text-center py-[9px] rounded-md"
                      style={{ border: "1px solid #342C82" }}
                    >
                      {purchaseOrder?.vendor || ""}
                    </p>
                  )}
                </div>
              )}
              {purchaseOrder?.id && (
                <div className="flex-1 flex flex-col">
                  <label className="Input_label">Value</label>
                  {isLoading ? (
                    <Skeleton height={40} baseColor="#e0e0e0" highlightColor="#ECE7FD" />
                  ) : (
                    <p
                      className="text-[#1F1F1F] bg-[#EDECF8] text-center py-[9px] rounded-md"
                      style={{ border: "1px solid #342C82" }}
                    >
                      {`${getAccountCurrency(settings)} ${displayDecimalFormatted(
                        settings,
                        purchaseOrder?.value
                      )}`}
                    </p>
                  )}
                </div>
              )}
            </div>

          </div>
          <div className="row align-items-end w-100 mb-5 gx-3">
            <div className="col-4">
              <Searchable
                label="Payment Code"
                SelectText="Code"
                selectWd="me-3"
                isRequired
                defaultVal={
                  cashflowLineItem.lineId
                    ? {
                      value: cashflowLineItem.lineId,
                      label: cashflowLineItem.label,
                    }
                    : null
                }
                searchText={(value) => {
                  handleCashflowLineSearch(value, {
                    changeRequestId: purchaseOrder?.changeRequest ? purchaseOrder?.changeRequest.id : null,
                  });
                }}
                options={cashflowLineOptions.map((option) => ({
                  label: `${getLabelByType("cashflowLineCategory", option.category)} - ${option.description}`,
                  value: option.id,
                }))}
                onChange={(line) => {
                  handleCashflowLineChange(line);
                }}
                loading={isLoading}
                errorMessage={formErrors.p_code}
              />
            </div>
            <div className="col-4">
              <Input
                icon={<CalendarIcon />}
                value={invoiceRegistry?.date}
                maxDate={invoiceRegistry.paidDate}
                type="date"
                isRequired
                handleChange={(value) => {
                  handleInputChange("date", value);
                }}
                label="Invoice Date"
                placeholder="Enter Invoice Date"
                loading={isLoading}
                errorMessage={formErrors.date}
              />
            </div>
            <div className="col-4">
              <Input
                icon={<CalendarIcon />}
                value={invoiceRegistry.paidDate}
                minDate={invoiceRegistry?.date}
                type="date"
                handleChange={(value) => {
                  handleInputChange("paidDate", value);
                }}
                label="Paid Date"
                placeholder="Enter Paid Date"
                loading={isLoading}
              />
            </div>
          </div>
          <div className="row w-100 mb-3 gx-3">
            <div className="col-4">
              <Input
                value={invoiceRegistry.account ?? ""}
                type="text"
                handleChange={(value) => {
                  handleInputChange("account", value);
                }}
                label="Account"
                placeholder="Enter Account"
                loading={isLoading}
              />
            </div>
            <div className="col-4">
              <Input
                inputwd=""
                icon={false}
                placeholder="Enter Supplier Invoice"
                value={invoiceRegistry.supplierInvId}
                type="text"
                handleChange={(value) => {
                  handleInputChange("supplierInvId", value);
                }}
                label="Supplier Inv #"
                loading={isLoading}
              />
            </div>
            <div className="col-4">
              <Input
                value={invoiceRegistry.internalInvId ?? ""}
                type="text"
                handleChange={(value) => {
                  handleInputChange("internalInvId", value);
                }}
                label="Internal In #"
                placeholder="Enter Internal Invoice"
                loading={isLoading}
              />
            </div>
          </div>
          <div className="row align-items-end w-100 mb-5 gx-3">
            <div className="col-4">
              <Input
                inputwd=""
                icon={false}
                placeholder="Enter Value Before Tax"
                value={invoiceRegistry.valueBeforeTax}
                type="number"
                handleChange={(value) => {
                  handleInputChange("valueBeforeTax", value);
                }}
                label="Value Before Tax"
                currency={true}
                loading={isLoading}
              />
            </div>
          </div>
          <div className="row w-100 gx-3 mb-3">
            <RichTextEditor
              label="Comments"
              content={invoiceRegistry.comments}
              onChange={(value) => {
                handleInputChange("comments", value);
              }}
              loading={isLoading}
            />
          </div>
          <EntityFileUpload
            entityId={selectedRow.id}
            entityType="Invoice"
            attachments={attachments}
            onAttachmentsChange={() => {
              if (selectedRow.id) {
                invoiceRegistryService
                  .fetchSingleInvoiceRegistryRequest(selectedRow.id)
                  .then((invoiceRegistryData) => {
                    setAttachments(invoiceRegistryData.attachments);
                  })
                  .catch((error) => {
                    console.error(
                      "Error fetching invoice registry data:",
                      error
                    );
                  });
              }
            }}
            disabled={isLoading || !selectedRow.id}
          />
        </div>
      </>
    );
  };

  const handleSearchTrigger = (searchTerm) => {
    if ((!tableSearch && !searchTerm) || (tableSearch === searchTerm)) return;

    let pageNumber = pageNum;
    if (!tableSearch || (tableSearch && !searchTerm) || (tableSearch !== searchTerm)) {
      setData([]); //clear existing data
      pageNumber = 1; //reset to first page
    }

    setTableSearch(searchTerm);
    fetchData(pageNumber, perPageItems, searchTerm, !searchTerm);
  };

  return (
    <ColumnConfigProvider tableName="InvoiceRegistry">
      <div className="listingScreen position-relative">
        <Table
          type="Invoice"
          rows={data || []}
          body={formBody()}
          searchHandler={handleSearch}
          saveBtnLabel="Save"
          cancelBtnLabel="Cancel"
          setPageNum={setPageNum}
          perPageItems={perPageItems}
          setIsCreate={(val) => {
            handleCreate(val);
          }}
          loading={pageLoading}
          modalLoading={loading}
          isCreate={isCreate}
          pagination={pagination}
          handleSave={() => handleSave()}
          handleDelete={(selectedRow) => handleDelete(selectedRow)}
          handleCancel={() => handleCancel()}
          setIsEdit={setIsEdit}
          setSelectedRow={setSelectedRow}
          projectId={projectID}
          parentType="Project"
          handleAddRow={handleAddRow}
          handleCancelAddRow={handleCancelAddRow}
          setData={setData}
          triggerSearch={handleSearchTrigger}
        />
      </div>
    </ColumnConfigProvider>
  );
};

export default InvoiceRegistry;
